// Rejecting a buyer's eBay cancel request (2026-10-28,
// docs/decisions/2026-10-28-agent-ebay-cancel-two-steps.md and
// docs/decisions/2026-10-28-ebay-cancel-reject-reason.md): the reject on
// eBay and a console order note that says the customer asked to cancel.
// Shared by the pipeline (server/src/ai/actions.js's FROM_EBAY cancellation),
// the agent's step previews (server/src/ai/shadow-step-previews.js) and the
// eBay client (server/src/channels/ebay/post-order.js).
//
// There is no reason to choose. The order's facts say whether it has
// shipped (outbound tracking, or a shipped-like status). Shipped: the note
// says so, with the carrier and tracking number, and the reject sends eBay
// the tracking number. Not recognized as shipped: the note gives no reason
// (the CSR writes it in the note) and the reject's body stays empty.
// Nothing else (delivered, can no longer be cancelled) is ever written for
// the person.
//
// eBay's reject (POST /post-order/v2/cancellation/{cancelId}/reject,
// RejectCancelRequest) takes no reason code, only an optional
// `trackingNumber` (a string) and an optional `shipmentDate` (a DateTime);
// "The shipmentDate and trackingNumber fields are optional in the request
// payload", and with neither the payload is `{}`. We never send
// shipmentDate: we know only the ship day, not its time, and a made-up time
// is not sent.

const SHIPPED_STATUS_RE = /ship|transit|deliver|complet|fulfil/i

// TRQ's carrier code as internal text writes it (the console note and the
// CSR's "Then: send tracking" line): Amazon Shipping (TRQ's AMZ) is ATS
// internally. Customer-facing carrier text is not this.
const NOTE_CARRIER = Object.freeze({ AMZ: 'ATS' })
function noteCarrier(carrier) {
  if (!carrier) return null
  return NOTE_CARRIER[String(carrier).trim().toUpperCase()] ?? carrier
}

// "1Z999AA10123456784 (UPS)" -> { number, carrier }. A placeholder such as
// "(tracking on the order)" is not a tracking number.
function firstTracking(tracking) {
  const list = Array.isArray(tracking) ? tracking : (tracking ? [tracking] : [])
  for (const t of list) {
    if (t && typeof t === 'object' && t.number) return { number: String(t.number), carrier: t.carrier ? String(t.carrier) : null }
    const m = /^\s*(\S+)(?:\s*\(([^)]+)\))?/.exec(String(t ?? ''))
    if (m && !/^\(/.test(m[1])) return { number: m[1], carrier: m[2] ? m[2].trim() : null }
  }
  return null
}

// What the order's facts say about the shipment: { shipped, tracking
// ({ number, carrier } or null) }. shipped when the order has a tracking
// number or a shipped-like status.
export function ebayCancelRejectShipment({ status = null, tracking = [] } = {}) {
  const t = firstTracking(tracking)
  const shipped = Boolean(t) || SHIPPED_STATUS_RE.test(String(status ?? ''))
  return { shipped, tracking: shipped ? t : null }
}

// The console order note. Shipped: "Customer requested to cancel in eBay
// (Cancel Id 5000000001). Rejected because the order had already shipped
// (UPS 1Z999AA10123456784)." (Amazon Shipping written ATS). Not recognized as
// shipped: "Customer requested to cancel in eBay (Cancel Id 5000000001).
// Rejected." (the CSR adds why).
export function ebayCancelRejectNote({ ebayCancelId, shipment = null } = {}) {
  const head = `Customer requested to cancel in eBay (Cancel Id ${ebayCancelId}).`
  if (!shipment?.shipped) return `${head} Rejected.`
  const t = shipment.tracking
  const carrier = t ? noteCarrier(t.carrier) : null
  return `${head} Rejected because the order had already shipped${t ? ` (${carrier ? `${carrier} ` : ''}${t.number})` : ''}.`
}

// The reject's eBay body: { trackingNumber } of a shipped order, `{}`
// otherwise (no tracking number: nothing is sent, as before).
export function ebayCancelRejectBody({ trackingNumber = null } = {}) {
  return trackingNumber ? { trackingNumber: String(trackingNumber) } : {}
}

// The reject step's params for what goes to eBay: { trackingNumber,
// trackingCarrier } of a shipped order with a tracking number, else {}.
export function ebayCancelRejectSendParams(shipment) {
  const number = shipment?.shipped ? shipment.tracking?.number : null
  if (!number) return {}
  return { trackingNumber: number, ...(shipment.tracking?.carrier ? { trackingCarrier: shipment.tracking.carrier } : {}) }
}

// The reject step's visible extra line, so the CSR sees what Process sends:
// "Then: send tracking UPS 1Z999AA10123456784 to eBay with the reject"
// (CSR-only, so Amazon Shipping reads ATS, as in the note). null when
// nothing is sent.
export function ebayCancelRejectTrackingLine({ trackingNumber = null, trackingCarrier = null } = {}) {
  if (!trackingNumber) return null
  const carrier = noteCarrier(trackingCarrier)
  return `Then: send tracking ${carrier ? `${carrier} ` : ''}${trackingNumber} to eBay with the reject`
}
